/**
 * Topics and tags of one message (the prototype's `pickTopics()`, shortcut T): a checkbox per topic with
 * its current score (a dash when it has none — the UI never invents a score), and the tags as a
 * comma-separated list. Unknown tag names are created (`tags.create` returns an existing tag with the
 * same folded name). Topic edits keep existing scores (engine.md §11.2).
 */

import { useEffect, useState, type FormEvent } from 'react';
import type { MessageId, TagId, TopicId } from '@digester/core';
import { DialogFrame, type DialogProps } from '../app/dialogs.js';
import { reportError } from '../app/errors.js';
import { showToast } from '../app/Toast.js';
import { useEngine, useEngineQuery } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { Icon } from '../icons/Icon.js';
import { Field } from '../ui/form.js';
import { useLookups } from './data.js';

export interface TopicsDialogProps {
  messageId: MessageId;
}

const fold = (s: string): string => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().trim();

export function TopicsDialog({ close, dialogId, messageId }: DialogProps & TopicsDialogProps) {
  const engine = useEngine();
  const lookups = useLookups();
  const detail = useEngineQuery(qk.message(messageId), (e) => e.messages.get(messageId)).data;
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [tagsText, setTagsText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const message = detail?.message;
  useEffect(() => {
    if (!message) return;
    setChosen((c) => c ?? new Set(message.topics.map((t) => t.topicId)));
    setTagsText((t) => t ?? message.tagIds.map((id) => lookups.tag(id)?.name ?? '').filter(Boolean).join(', '));
  }, [message, lookups]);

  const topics = lookups.topics.filter((t) => t.archivedAt === null || message?.topics.some((x) => x.topicId === t.id));
  const scoreOf = (id: string): number | null => {
    const visible = message?.topics.find((t) => t.topicId === id);
    if (visible) return visible.score;
    return message?.scores?.[id] ?? null;
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!message || !chosen || tagsText === null) return;
    setBusy(true);
    try {
      const before = new Set(message.topics.map((t) => t.topicId));
      const topicIds = [...chosen] as TopicId[];
      const topicsChanged = topicIds.length !== before.size || topicIds.some((id) => !before.has(id as TopicId));

      const names = [...new Map(tagsText.split(',').map((t) => t.trim()).filter(Boolean).map((n) => [fold(n), n])).values()];
      const tagIds: TagId[] = [];
      for (const name of names) {
        const existing = lookups.tags.find((t) => fold(t.name) === fold(name));
        tagIds.push(existing ? existing.id : (await engine.tags.create({ name, icon: 'tag' })).id);
      }
      const beforeTags = new Set<string>(message.tagIds);
      const tagsChanged = tagIds.length !== beforeTags.size || tagIds.some((id) => !beforeTags.has(id));

      // One toast, one Undo for both changes (undone tags first, then topics).
      const undoIds: Array<string | null> = [];
      if (topicsChanged) undoIds.push((await engine.messages.setTopics(message.id, topicIds)).undoId);
      if (tagsChanged) undoIds.push((await engine.messages.setTags(message.id, tagIds)).undoId);
      close();
      if (topicsChanged || tagsChanged) showToast(topicsChanged && tagsChanged ? 'Topics and tags updated' : topicsChanged ? 'Topics updated' : 'Tags updated', undoIds);
    } catch (error) {
      reportError(error);
      setBusy(false);
    }
  };

  return (
    <DialogFrame title="Topics" onClose={close} dialogId={dialogId}>
      <form id="topics-form" onSubmit={(e) => void submit(e)}>
        <div className="topic-picker">
          {topics.map((t) => {
            const score = scoreOf(t.id);
            return (
              <label key={t.id}>
                <input
                  type="checkbox"
                  name="topics"
                  value={t.id}
                  checked={chosen?.has(t.id) ?? false}
                  disabled={!chosen}
                  onChange={(ev) =>
                    setChosen((c) => {
                      const next = new Set(c ?? []);
                      if (ev.target.checked) next.add(t.id);
                      else next.delete(t.id);
                      return next;
                    })
                  }
                />
                <span style={{ color: t.color }}>
                  <Icon name={t.icon} />
                </span>
                <span>{t.name}</span>
                <b title={score === null ? 'No model score' : 'Topic relevance out of 100'}>{score ?? '—'}</b>
              </label>
            );
          })}
          {topics.length === 0 ? <p className="subtle">No topics yet. Add them in Settings → Topics.</p> : null}
        </div>
        <Field label="Tags · comma separated" name="tags" value={tagsText ?? ''} onChange={setTagsText} disabled={tagsText === null} autoComplete="off" />
        <div className="dialog-footer">
          <button type="button" className="secondary-button" onClick={close}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy || !message}>
            Apply
          </button>
        </div>
      </form>
    </DialogFrame>
  );
}
